import React, { useEffect, useState, useRef } from "react";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faAdd,
  faMinus,
  faPenToSquare,
  faRedo,
} from "@fortawesome/free-solid-svg-icons";
import Box from "@mui/material/Box";
import TextField from "@mui/material/TextField";
import { FormControlLabel, FormGroup, Modal, Switch } from "@mui/material";
import { useDispatch, useSelector } from "react-redux";
import { useTranslation } from "react-i18next";

import PreviewImageUI from "../../../components/ui/preview-image/preview-image";
import FieldsetUI from "../../../components/ui/fieldset/fieldset";
import ButtonUI from "../../../components/ui/button/button";
import { svCreatePersonnel } from "../../../services/personnel.service";
import SwalUI from "../../../components/ui/swal-ui/swal-ui";
import "./personnel-modal.scss";

import { appActions } from "../../../store/app-slice";

function PersonnelAddModal({ isOpen, setClose, setRefreshData, maxPriority }) {
  const addDataDefault = {
    name: "",
    position: "",
    email: "",
    expertise: "",
    priority: maxPriority + 1,
    display: true,
    is_manager: false,
  }
  const thumbnailDefault = { thumbnail: true, src: "", file: null, name: null, remove: false }

  const { t } = useTranslation("personnel-page");
  const [previews, setPreviews] = useState(thumbnailDefault);
  const [isError, setIsError] = useState({
    profile_image: false,
    name: false,
    position: false,
    is_manager: false,
  });
  const dispatch = useDispatch();
  const uploadPath = useSelector((state) => state.app.uploadPath);
  const language = useSelector((state) => state.app.language);
  const defaultLanguage = useSelector((state) => state.app.defaultLanguage);
  const [addData, setAddData] = useState(addDataDefault);
  const [isFetching, setIsFetching] = useState(false);
  const scrollRef = useRef(null);

  useEffect(() => {
    
  }, [isOpen]);

  const setPreviewHandler = (data) => {
    setAddData({ ...addData, imageName: data.file ? data.file.name : "" });
    setPreviews(data);
  };

  const priorityHandler = (isAdding) => {
    if (isAdding) {
      setAddData((prevState) => {
        return { ...prevState, priority: addData.priority + 1 };
      });
    } else if (addData.priority > 1) {
      setAddData((prevState) => {
        return { ...prevState, priority: addData.priority - 1 };
      });
    }
  };

  const addValidators = () => {
    let isValid = true;

    let error = {
      ...isError,
      name: addData.name.trim().length < 1,
      position: addData.position.trim().length < 1,
      profile_image: previews.file ? false : true,
    };

    setIsError(() => error);
    const hasError = Object.values(error).find((err) => err === true);
    if (hasError) isValid = false;

    if (isValid) {
      const formData = new FormData();
      if (previews.file) {
        formData.append("profile_image", previews.file);
      }
      formData.append("name", addData.name || "");
      formData.append("position", addData.position);
      formData.append("email", addData.email || "");
      formData.append("expertise", addData.expertise || "");
      formData.append("display", addData.display ? 1 : 0);
      formData.append("is_manager", addData.is_manager ? 1 : 0);
      formData.append("priority", addData.priority);
      formData.append("language", defaultLanguage);

      saveHandler(formData);
    } else {
      scrollToTop();
    }
  };

  const scrollToTop = () => {
    if (scrollRef.current) {
      scrollRef.current.scrollTo({
        top: 0,
        behavior: "smooth",
      });
    }
  };

  const saveHandler = (formData) => {
    setIsFetching(true);
    svCreatePersonnel(formData).then((res) => {
      setClose(false);
      setIsFetching(false);

      if (res.status === 201) {
        SwalUI({ status: res.data.status, description: res.data.description });
        setRefreshData((prev) => prev + 1);
      } else {
        SwalUI({ status: res.data.status, description: res.data.description });
      }
    });
  };

  return (
    <>
      <Modal
        disableEnforceFocus
        open={isOpen}
        onClose={() =>
          setClose(false)
        }
        aria-labelledby="parent-modal-title"
        aria-describedby="parent-modal-description"
      >
        <Box id="personnel-add-modal">
          <section id="personnel-add-page">
            <div className="card-control">
              <div className="card-head">
                <div className="head-action">
                  <h2 className="head-title">
                    <FontAwesomeIcon icon={faPenToSquare} /> {t("แก้ไขโปรไฟล์")}
                  </h2>
                </div>
              </div>
              <div className="card-body" ref={scrollRef}>
                <Box
                  component="form"
                  noValidate
                  autoComplete="off"
                  sx={{ "& .MuiTextField-root": { m: 1, width: "25ch" } }}
                >
                  <FieldsetUI
                    className={`image-setting ${
                      isError.profile_image ? "error" : ""
                    }`}
                    label={t("รูปภาพโปรไฟล์")}
                  >
                    <PreviewImageUI
                      className="add-image"
                      previews={previews}
                      setPreviews={setPreviewHandler}
                    />
                  </FieldsetUI>

                  <div className="personnel-details">
                    <div className="input-full">
                      <TextField
                        onChange={(e) =>
                          setAddData((prevState) => {
                            return {
                              ...prevState,
                              name: e.target.value,
                            };
                          })
                        }
                        value={addData.name}
                        className="text-field-custom"
                        fullWidth={true}
                        error={isError.name}
                        id="ad-name"
                        label="ชื่อ"
                        size="small"
                      />
                    </div>
                    <div className="input-full">
                      <TextField
                        onChange={(e) =>
                          setAddData((prevState) => {
                            return {
                              ...prevState,
                              position: e.target.value,
                            };
                          })
                        }
                        value={addData.position}
                        className="text-field-custom"
                        fullWidth={true}
                        error={isError.position}
                        id="ad-position"
                        label="ตำแหน่ง"
                        size="small"
                      />
                    </div>
                    <div className="input-full">
                      <TextField
                        onChange={(e) =>
                          setAddData((prevState) => {
                            return {
                              ...prevState,
                              email: e.target.value,
                            };
                          })
                        }
                        value={addData.email}
                        className="text-field-custom"
                        fullWidth={true}
                        error={false}
                        id="ad-email"
                        label="อีเมล"
                        size="small"
                      />
                    </div>
                    <div className="input-full">
                      <TextField
                        onChange={(e) =>
                          setAddData((prevState) => {
                            return {
                              ...prevState,
                              expertise: e.target.value,
                            };
                          })
                        }
                        value={addData.expertise}
                        className="text-field-custom"
                        fullWidth={true}
                        error={false}
                        id="ad-expertise"
                        label="ความเชี่ยวชาญ"
                        size="small"
                        multiline
                      />
                    </div>

                    <div className="group-settings">
                      <div className="setting-controls">
                        <div className="switch-form">
                          <FormGroup>
                            <FormControlLabel
                              control={
                                <Switch
                                  onChange={(e) =>
                                    setAddData({
                                      ...addData,
                                      is_manager: e.target.checked,
                                    })
                                  }
                                  checked={addData.is_manager ? true : false}
                                />
                              }
                              label={t("หัวหน้าสาขา")}
                              labelPlacement="start"
                            />
                          </FormGroup>
                        </div>
                        <div className="switch-form">
                          <FormGroup>
                            <FormControlLabel
                              control={
                                <Switch
                                  onChange={(e) =>
                                    setAddData({
                                      ...addData,
                                      display: e.target.checked,
                                    })
                                  }
                                  checked={addData.display ? true : false}
                                />
                              }
                              label={t("การแสดงผล")}
                              labelPlacement="start"
                            />
                          </FormGroup>
                        </div>
                        <div className="input-group-settings">
                          <div className="inp">
                            <ButtonUI
                              color="error"
                              onClick={(e) => priorityHandler(false)}
                            >
                              <FontAwesomeIcon icon={faMinus} />
                            </ButtonUI>
                            <span className="title">
                              {t("ลำดับ")} {addData.priority}
                            </span>
                            <ButtonUI onClick={(e) => priorityHandler(true)}>
                              <FontAwesomeIcon icon={faAdd} />
                            </ButtonUI>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                </Box>
              </div>
              <div className="btn-action">
                <ButtonUI
                  isLoading={isFetching}
                  onClick={addValidators}
                  className="btn-save"
                  on="save"
                  width="md"
                >
                  {t("บันทึก")}
                </ButtonUI>
                <ButtonUI
                  onClick={() =>
                    setClose(false)
                  }
                  icon={<FontAwesomeIcon icon={faRedo} />}
                  className="btn-cancel"
                  on="cancel"
                  width="md"
                >
                  {t("ยกเลิก")}
                </ButtonUI>
              </div>
            </div>
          </section>
        </Box>
      </Modal>
    </>
  );
}

export default PersonnelAddModal;
